import React, { useState } from 'react';

type ColumnType = 'string' | 'number' | 'date';

interface Props {
  headers: string[];
  rows: Array<Record<string, any>>;
  onConfigChange?: (cfg: {
    include: Record<string, boolean>;
    types: Record<string, ColumnType>;
    excludedRowIndices: number[];
  }) => void;
  className?: string;
  defaultTypes?: Record<string, ColumnType>;
}

export const DataGridPreview: React.FC<Props> = ({ headers, rows, onConfigChange, className, defaultTypes }) => {
  const [includeCols, setIncludeCols] = useState<Record<string, boolean>>(() => Object.fromEntries(headers.map(h => [h, true])));
  const [types, setTypes] = useState<Record<string, ColumnType>>(() => Object.fromEntries(headers.map(h => [h, (defaultTypes && defaultTypes[h]) || 'string'])));
  const [rowInclude, setRowInclude] = useState<boolean[]>(() => rows.map(() => true));

  const update = () => {
    onConfigChange?.({
      include: includeCols,
      types,
      excludedRowIndices: rowInclude.map((v, i) => (v ? -1 : i)).filter(i => i >= 0),
    });
  };

  const setColInclude = (h: string, v: boolean) => {
    setIncludeCols(prev => { const n = { ...prev, [h]: v }; return n; });
    setTimeout(update, 0);
  };
  const setColType = (h: string, v: ColumnType) => {
    setTypes(prev => { const n = { ...prev, [h]: v }; return n; });
    setTimeout(update, 0);
  };
  const setRow = (idx: number, v: boolean) => {
    setRowInclude(prev => { const n = [...prev]; n[idx] = v; return n; });
    setTimeout(update, 0);
  };

  return (
    <div className={className}>
      <div className="mb-2 flex items-center justify-between">
        <div className="text-sm text-gray-600 dark:text-gray-400">Rows: {rows.length} • Columns: {headers.length}</div>
        <div className="text-xs text-gray-500">Toggle columns and types; uncheck rows to exclude.</div>
      </div>
      <div className="overflow-auto border border-gray-200 dark:border-gray-700 rounded">
        <table className="min-w-full text-xs">
          <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
            <tr>
              <th className="p-2 text-left">Include</th>
              {headers.map(h => (
                <th key={h} className="p-2 text-left whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <input type="checkbox" checked={includeCols[h] !== false} onChange={e => setColInclude(h, e.target.checked)} />
                    <span className="font-medium text-gray-700 dark:text-gray-300">{h}</span>
                    <select value={types[h]}
                      onChange={e => setColType(h, e.target.value as ColumnType)}
                      className="border border-gray-300 dark:border-gray-600 rounded px-1 py-0.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                      <option value="string">text</option>
                      <option value="number">number</option>
                      <option value="date">date</option>
                    </select>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={idx} className={idx % 2 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50/30 dark:bg-gray-800/40'}>
                <td className="p-2 align-top"><input type="checkbox" checked={rowInclude[idx]} onChange={e => setRow(idx, e.target.checked)} /></td>
                {headers.map(h => (
                  <td key={h} className="p-2 align-top text-gray-800 dark:text-gray-200" style={{display: includeCols[h] === false ? 'none' : undefined}}>
                    {String(r[h] ?? '')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
